import styled from "styled-components";
import {topbar, zIndex} from "theme/tokens";

/**
 * Externalized styles for {@link Banners} (owner externalization mandate, 2026-07-12).
 *
 * ## Floating, not in flow (ui#1765)
 *
 * The region is `position: absolute`, anchored to the same scroll container that anchors `NavBar`'s
 * absolute `Topbar`, so a banner OVERLAYS the page instead of pushing it down (the in-flow region
 * shifted every route's content whenever a banner appeared) and scrolls away with the bar it hangs
 * under. Floating also retires the `--tf-banner-clearance` publication ui#1188 added for fixed
 * chrome: the region no longer takes flow space, so there is nothing for fixed surfaces to clear.
 *
 * ## The one place the top bar's overlap is paid for (ui#1188)
 *
 * The bar overlays the top of the shell, and its `pointer-events: none` does not extend to its
 * CHILDREN — whichever child happened to sit over a banner swallowed its clicks (`PastDueBanner`'s
 * "Update card" could not be pressed). The interceptor varied with the viewport — the theme toggle
 * at one width, the Resources label at another — so what is fixed here is the RESERVATION: the
 * region pads itself by the bar's own height token, so banners start below the bar and the two can
 * not drift apart. `zIndex.banner` settles the remaining overlap the other way — a banner child
 * beats bar children — and the region mirrors the bar's own `pointer-events` split so the
 * transparent padding band and the gutters around a banner never swallow clicks meant for the bar
 * or the page.
 *
 * ## Why it collapses when empty
 *
 * React renders no node at all for the falsy/`null` branches the children return, so "this region
 * is showing a banner" and "this region has a child element" are the same fact, and `:empty` reads
 * it directly instead of a second copy of each banner's visibility rule.
 *
 * The 640px breakpoint mirrors `NavBar.styles`' own — the width at which the bar takes its mobile
 * height — and is written as the plain literal every other responsive rule in this codebase uses.
 */
export const BannerRegion = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: ${zIndex.banner};
	pointer-events: none;
	padding-top: ${topbar.height};

	> * {
		pointer-events: auto;
	}

	@media (max-width: 640px) {
		padding-top: ${topbar.heightMobile};
	}

	&:empty {
		padding-top: 0;
	}
`;
